import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Briefcase, Shield, FileText, BookOpen, GraduationCap,
  CheckCircle, Clock, ArrowLeft, ArrowRight, Upload, Camera,
  Phone, Mail, MapPin, Car, Shirt, Package, ShoppingBag,
  AlertCircle, Star, Award, Zap, Lock, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import api from '../services/api';

// 8-Step Onboarding Flow
const STEPS = [
  { id: 1, title: 'Account', subtitle: 'Create your profile', icon: User },
  { id: 2, title: 'Services', subtitle: 'Select what you do', icon: Briefcase },
  { id: 3, title: 'Identity', subtitle: 'Verify who you are', icon: Shield },
  { id: 4, title: 'Documents', subtitle: 'Upload required docs', icon: FileText },
  { id: 5, title: 'Policies', subtitle: 'Review guidelines', icon: BookOpen },
  { id: 6, title: 'Training', subtitle: 'Complete modules', icon: GraduationCap },
  { id: 7, title: 'Review', subtitle: 'Check your info', icon: Eye },
  { id: 8, title: 'Done', subtitle: 'Get activated', icon: Award }
];

const SERVICE_OPTIONS = [
  { id: 'rides', label: 'Rides & Transportation', icon: Car, description: 'Drive customers safely to their destinations', color: '#3B82F6' },
  { id: 'courier', label: 'Courier & Delivery', icon: Package, description: 'Deliver packages and documents', color: '#06B6D4' },
  { id: 'errands', label: 'Errands & Tasks', icon: ShoppingBag, description: 'Run errands and complete tasks', color: '#EC4899' },
  { id: 'laundry', label: 'Laundry Pickup', icon: Shirt, description: 'Pick up and deliver laundry', color: '#8B5CF6' }
];

const WorkerOnboardingWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form data across all steps
  const [formData, setFormData] = useState({
    // Step 1: Account
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    address: '',
    city: '',
    state: 'NC',
    zip_code: '',
    
    // Step 2: Services
    service_types: [],
    experience_years: 0,
    has_vehicle: false,
    vehicle_type: '',
    vehicle_year: '',
    
    // Step 3: Identity
    date_of_birth: '',
    ssn_last_four: '',
    drivers_license_number: '',
    dl_state: 'NC',
    dl_expiry: '',
    
    // Step 4: Documents
    id_document: null,
    drivers_license_photo: null,
    vehicle_insurance: null,
    vehicle_registration: null,
    background_consent: false,
    
    // Step 5: Policies
    agreed_to_policies: false,
    agreed_to_terms: false,
    agreed_to_privacy: false,
    agreed_to_conduct: false,
    
    // Step 6: Training (tracked separately)
    training_completed: []
  });
  
  // Training modules
  const [trainingModules] = useState([
    { id: 'safety', title: 'Safety Guidelines', duration: '5 min', required: true },
    { id: 'customer_service', title: 'Customer Service Excellence', duration: '8 min', required: true },
    { id: 'app_usage', title: 'Using the Worker App', duration: '6 min', required: true },
    { id: 'policies', title: 'Company Policies', duration: '4 min', required: true }
  ]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleService = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      service_types: prev.service_types.includes(serviceId)
        ? prev.service_types.filter(s => s !== serviceId)
        : [...prev.service_types, serviceId]
    }));
  };

  const handleFileUpload = (field, file) => {
    if (file) {
      // In production, upload to server and store URL
      // For now, store file reference
      updateFormData(field, file);
      toast.success(`${field.replace(/_/g, ' ')} uploaded`);
    }
  };

  const completeTraining = (moduleId) => {
    if (!formData.training_completed.includes(moduleId)) {
      setFormData(prev => ({
        ...prev,
        training_completed: [...prev.training_completed, moduleId]
      }));
      toast.success('Training module completed!');
    }
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.full_name || !formData.email || !formData.phone || !formData.password) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (formData.password !== formData.confirm_password) {
          toast.error('Passwords do not match');
          return false;
        }
        if (formData.password.length < 8) {
          toast.error('Password must be at least 8 characters');
          return false;
        }
        return true;
        
      case 2:
        if (formData.service_types.length === 0) {
          toast.error('Please select at least one service');
          return false;
        }
        if (formData.service_types.includes('rides') && !formData.has_vehicle) {
          toast.error('Vehicle is required for ride services');
          return false;
        }
        return true;
        
      case 3:
        if (!formData.date_of_birth || !formData.drivers_license_number) {
          toast.error('Please fill in all identity information');
          return false;
        }
        return true;
        
      case 4:
        if (!formData.id_document || !formData.background_consent) {
          toast.error('Please upload required documents and consent to background check');
          return false;
        }
        return true;
        
      case 5:
        if (!formData.agreed_to_policies || !formData.agreed_to_terms || !formData.agreed_to_conduct) {
          toast.error('Please agree to all required policies');
          return false;
        }
        return true;
        
      case 6:
        const requiredModules = trainingModules.filter(m => m.required);
        const allCompleted = requiredModules.every(m => formData.training_completed.includes(m.id));
        if (!allCompleted) {
          toast.error('Please complete all required training modules');
          return false;
        }
        return true;
        
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 8));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const submitApplication = async () => {
    setLoading(true);
    try {
      const applicationData = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        service_types: formData.service_types,
        experience_years: formData.experience_years,
        has_vehicle: formData.has_vehicle,
        vehicle_type: formData.vehicle_type,
        date_of_birth: formData.date_of_birth,
        drivers_license_number: formData.drivers_license_number,
        dl_state: formData.dl_state,
        dl_expiry: formData.dl_expiry,
        background_consent: formData.background_consent,
        training_completed: formData.training_completed
      };
      
      await api.post('/applications/submit', applicationData);
      toast.success('Application submitted successfully!');
      setCurrentStep(8);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const getStepProgress = () => {
    return Math.round((currentStep / 8) * 100);
  };

  return (
    <div className="min-h-screen bg-[#030303] relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#D4AF37]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#F4C2C2]/10 rounded-full blur-[120px]" />
      </div>

      {/* Header */}
      <div className="relative z-10 bg-black/40 backdrop-blur-2xl border-b border-white/10 sticky top-0">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')} 
            className="text-white/60 hover:text-white flex items-center gap-2 transition-colors"
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Home</span>
          </button>
          <div className="text-center">
            <h1 className="text-lg font-bold text-white font-['Cormorant_Garamond']">Join NC Help Hub</h1>
            <p className="text-xs text-white/50">Worker Onboarding</p>
          </div>
          <div className="w-20 text-right">
            <span className="text-[#D4AF37] font-bold">{getStepProgress()}%</span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 pt-4">
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F4C2C2]"
            initial={{ width: 0 }}
            animate={{ width: `${getStepProgress()}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Step Indicators */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between overflow-x-auto pb-2">
          {STEPS.map((step, idx) => (
            <React.Fragment key={step.id}>
              <motion.div 
                className="flex flex-col items-center min-w-[60px]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
                  currentStep > step.id 
                    ? 'bg-green-500 text-white' 
                    : currentStep === step.id 
                      ? 'bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] text-black shadow-lg shadow-[#D4AF37]/30' 
                      : 'bg-white/5 text-white/40 border border-white/10'
                }`}>
                  {currentStep > step.id ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span className={`mt-1 text-[10px] font-medium text-center ${
                  currentStep >= step.id ? 'text-white' : 'text-white/40'
                }`}>
                  {step.title}
                </span>
              </motion.div>
              {idx < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 rounded-full transition-all duration-500 min-w-[20px] ${
                  currentStep > step.id ? 'bg-green-500' : 'bg-white/10'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 pb-24">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
            className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-3xl p-6 sm:p-8"
          >
            {/* Step 1: Account */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 to-[#F4C2C2]/20 flex items-center justify-center border border-[#D4AF37]/30">
                    <User className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Create Your Account</h2>
                    <p className="text-white/50 text-sm">Let's start with your basic information</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label className="text-white/70 mb-2 block">Full Name *</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => updateFormData('full_name', e.target.value)}
                      placeholder="Your full legal name"
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl placeholder:text-white/50"
                      data-testid="onboard-name"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white/70 mb-2 block">Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateFormData('email', e.target.value)}
                      placeholder="email@example.com"
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl placeholder:text-white/50"
                      data-testid="onboard-email"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white/70 mb-2 block">Phone *</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => updateFormData('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl placeholder:text-white/50"
                      data-testid="onboard-phone"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white/70 mb-2 block">Password *</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => updateFormData('password', e.target.value)}
                        placeholder="Min 8 characters"
                        className="bg-white/5 border-white/10 text-white h-12 rounded-xl pr-12 placeholder:text-white/50"
                        data-testid="onboard-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white/70 mb-2 block">Confirm Password *</Label>
                    <Input
                      type="password"
                      value={formData.confirm_password}
                      onChange={(e) => updateFormData('confirm_password', e.target.value)}
                      placeholder="Confirm password"
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl placeholder:text-white/50"
                      data-testid="onboard-confirm-password"
                    />
                  </div>
                  
                  <div className="sm:col-span-2">
                    <Label className="text-white/70 mb-2 block">Street Address</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => updateFormData('address', e.target.value)}
                      placeholder="123 Main St"
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl placeholder:text-white/50"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white/70 mb-2 block">City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => updateFormData('city', e.target.value)}
                      placeholder="City"
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl placeholder:text-white/50"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70 mb-2 block">State</Label>
                      <Input
                        value={formData.state}
                        onChange={(e) => updateFormData('state', e.target.value)}
                        placeholder="NC"
                        className="bg-white/5 border-white/10 text-white h-12 rounded-xl placeholder:text-white/50"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70 mb-2 block">ZIP</Label>
                      <Input
                        value={formData.zip_code}
                        onChange={(e) => updateFormData('zip_code', e.target.value)}
                        placeholder="27601"
                        className="bg-white/5 border-white/10 text-white h-12 rounded-xl placeholder:text-white/50"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Services */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 to-[#F4C2C2]/20 flex items-center justify-center border border-[#D4AF37]/30">
                    <Briefcase className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Select Your Services</h2>
                    <p className="text-white/50 text-sm">What services would you like to provide?</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {SERVICE_OPTIONS.map(service => (
                    <button
                      key={service.id}
                      onClick={() => toggleService(service.id)}
                      className={`p-5 rounded-2xl border text-left transition-all duration-300 ${
                        formData.service_types.includes(service.id)
                          ? 'border-[#D4AF37] shadow-lg'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                      style={{
                        background: formData.service_types.includes(service.id)
                          ? `linear-gradient(135deg, ${service.color}20, ${service.color}10)`
                          : 'rgba(255,255,255,0.02)'
                      }}
                      data-testid={`service-${service.id}`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-xl" style={{ background: `${service.color}30` }}>
                          <service.icon className="w-5 h-5" style={{ color: service.color }} />
                        </div>
                        {formData.service_types.includes(service.id) && (
                          <CheckCircle className="w-5 h-5 text-[#D4AF37] ml-auto" />
                        )}
                      </div>
                      <p className="font-semibold text-white">{service.label}</p>
                      <p className="text-xs text-white/50 mt-1">{service.description}</p>
                    </button>
                  ))}
                </div>

                <div className="space-y-4 pt-4">
                  <div>
                    <Label className="text-white/70 mb-2 block">Years of Experience</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.experience_years}
                      onChange={(e) => updateFormData('experience_years', parseInt(e.target.value) || 0)}
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl w-32"
                    />
                  </div>

                  {formData.service_types.includes('rides') && (
                    <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 space-y-4">
                      <div className="flex items-center gap-3">
                        <Car className="w-5 h-5 text-blue-400" />
                        <span className="text-white font-medium">Vehicle Information</span>
                      </div>
                      
                      <label className="flex items-center gap-3 cursor-pointer">
                        <Checkbox
                          checked={formData.has_vehicle}
                          onCheckedChange={(checked) => updateFormData('has_vehicle', checked)}
                          data-testid="has-vehicle-checkbox"
                        />
                        <span className="text-white/80">I have my own vehicle</span>
                      </label>
                      
                      {formData.has_vehicle && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-white/70 mb-2 block">Vehicle Type</Label>
                            <Input
                              value={formData.vehicle_type}
                              onChange={(e) => updateFormData('vehicle_type', e.target.value)}
                              placeholder="e.g., Toyota Camry"
                              className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                            />
                          </div>
                          <div>
                            <Label className="text-white/70 mb-2 block">Vehicle Year</Label>
                            <Input
                              value={formData.vehicle_year}
                              onChange={(e) => updateFormData('vehicle_year', e.target.value)}
                              placeholder="2020"
                              className="bg-black/30 border-white/10 text-white h-11 rounded-xl placeholder:text-white/50"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Identity */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 to-[#F4C2C2]/20 flex items-center justify-center border border-[#D4AF37]/30">
                    <Shield className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Identity Verification</h2>
                    <p className="text-white/50 text-sm">We need to verify your identity for safety</p>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30 mb-6">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-green-400" />
                    <p className="text-green-400 text-sm">Your information is encrypted and secure</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 mb-2 block">Date of Birth *</Label>
                    <Input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => updateFormData('date_of_birth', e.target.value)}
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl [color-scheme:dark]"
                      data-testid="onboard-dob"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white/70 mb-2 block">SSN Last 4 Digits</Label>
                    <Input
                      maxLength={4}
                      value={formData.ssn_last_four}
                      onChange={(e) => updateFormData('ssn_last_four', e.target.value.replace(/\D/g, ''))}
                      placeholder="XXXX"
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl placeholder:text-white/50"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white/70 mb-2 block">Driver's License Number *</Label>
                    <Input
                      value={formData.drivers_license_number}
                      onChange={(e) => updateFormData('drivers_license_number', e.target.value)}
                      placeholder="License number"
                      className="bg-white/5 border-white/10 text-white h-12 rounded-xl placeholder:text-white/50"
                      data-testid="onboard-dl"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70 mb-2 block">DL State</Label>
                      <Input
                        value={formData.dl_state}
                        onChange={(e) => updateFormData('dl_state', e.target.value)}
                        placeholder="NC"
                        className="bg-white/5 border-white/10 text-white h-12 rounded-xl placeholder:text-white/50"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70 mb-2 block">Expiry Date</Label>
                      <Input
                        type="date"
                        value={formData.dl_expiry}
                        onChange={(e) => updateFormData('dl_expiry', e.target.value)}
                        className="bg-white/5 border-white/10 text-white h-12 rounded-xl [color-scheme:dark]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Documents */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 to-[#F4C2C2]/20 flex items-center justify-center border border-[#D4AF37]/30">
                    <FileText className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Upload Documents</h2>
                    <p className="text-white/50 text-sm">Required documents for verification</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* ID Document */}
                  <div className="p-4 rounded-xl border border-white/10 hover:border-white/20 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${formData.id_document ? 'bg-green-500/20' : 'bg-white/5'}`}>
                          {formData.id_document ? (
                            <CheckCircle className="w-5 h-5 text-green-400" />
                          ) : (
                            <Camera className="w-5 h-5 text-white/50" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">Government ID *</p>
                          <p className="text-xs text-white/50">Driver's license or state ID</p>
                        </div>
                      </div>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload('id_document', e.target.files?.[0])}
                          data-testid="upload-id-doc"
                        />
                        <span className="px-4 py-2 rounded-xl bg-white/10 text-white text-sm hover:bg-white/20 transition-colors">
                          {formData.id_document ? 'Replace' : 'Upload'}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Driver's License Photo (if rides selected) */}
                  {formData.service_types.includes('rides') && (
                    <>
                      <div className="p-4 rounded-xl border border-white/10 hover:border-white/20 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${formData.vehicle_insurance ? 'bg-green-500/20' : 'bg-white/5'}`}>
                              {formData.vehicle_insurance ? (
                                <CheckCircle className="w-5 h-5 text-green-400" />
                              ) : (
                                <FileText className="w-5 h-5 text-white/50" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">Vehicle Insurance</p>
                              <p className="text-xs text-white/50">Current insurance card</p>
                            </div>
                          </div>
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              className="hidden"
                              onChange={(e) => handleFileUpload('vehicle_insurance', e.target.files?.[0])}
                            />
                            <span className="px-4 py-2 rounded-xl bg-white/10 text-white text-sm hover:bg-white/20 transition-colors">
                              {formData.vehicle_insurance ? 'Replace' : 'Upload'}
                            </span>
                          </label>
                        </div>
                      </div>

                      <div className="p-4 rounded-xl border border-white/10 hover:border-white/20 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${formData.vehicle_registration ? 'bg-green-500/20' : 'bg-white/5'}`}>
                              {formData.vehicle_registration ? (
                                <CheckCircle className="w-5 h-5 text-green-400" />
                              ) : (
                                <FileText className="w-5 h-5 text-white/50" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">Vehicle Registration</p>
                              <p className="text-xs text-white/50">Current registration</p>
                            </div>
                          </div>
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              className="hidden"
                              onChange={(e) => handleFileUpload('vehicle_registration', e.target.files?.[0])}
                            />
                            <span className="px-4 py-2 rounded-xl bg-white/10 text-white text-sm hover:bg-white/20 transition-colors">
                              {formData.vehicle_registration ? 'Replace' : 'Upload'}
                            </span>
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Background Check Consent */}
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 mt-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={formData.background_consent}
                      onCheckedChange={(checked) => updateFormData('background_consent', checked)}
                      className="mt-1"
                      data-testid="background-consent"
                    />
                    <div>
                      <p className="text-white font-medium">Background Check Consent *</p>
                      <p className="text-sm text-white/60 mt-1">
                        I authorize NC Help Hub to conduct a background check as part of the application process. 
                        I understand this may include criminal history, driving record, and identity verification.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Step 5: Policies */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 to-[#F4C2C2]/20 flex items-center justify-center border border-[#D4AF37]/30">
                    <BookOpen className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Review & Agree</h2>
                    <p className="text-white/50 text-sm">Please review and accept our policies</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 rounded-xl border border-white/10">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={formData.agreed_to_terms}
                        onCheckedChange={(checked) => updateFormData('agreed_to_terms', checked)}
                        className="mt-1"
                        data-testid="agree-terms"
                      />
                      <div>
                        <p className="text-white font-medium">Terms of Service *</p>
                        <p className="text-sm text-white/60 mt-1">
                          I have read and agree to the NC Help Hub Terms of Service, including the worker agreement 
                          and payment terms.
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="p-4 rounded-xl border border-white/10">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={formData.agreed_to_privacy}
                        onCheckedChange={(checked) => updateFormData('agreed_to_privacy', checked)}
                        className="mt-1"
                      />
                      <div>
                        <p className="text-white font-medium">Privacy Policy</p>
                        <p className="text-sm text-white/60 mt-1">
                          I understand how my personal information will be collected, used, and protected.
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="p-4 rounded-xl border border-white/10">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={formData.agreed_to_conduct}
                        onCheckedChange={(checked) => updateFormData('agreed_to_conduct', checked)}
                        className="mt-1"
                        data-testid="agree-conduct"
                      />
                      <div>
                        <p className="text-white font-medium">Code of Conduct *</p>
                        <p className="text-sm text-white/60 mt-1">
                          I agree to maintain professional conduct, treat customers with respect, and uphold 
                          NC Help Hub's service standards at all times.
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="p-4 rounded-xl border border-white/10">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={formData.agreed_to_policies}
                        onCheckedChange={(checked) => updateFormData('agreed_to_policies', checked)}
                        className="mt-1"
                        data-testid="agree-policies"
                      />
                      <div>
                        <p className="text-white font-medium">Safety & Service Policies *</p>
                        <p className="text-sm text-white/60 mt-1">
                          I understand and agree to follow all safety guidelines, service protocols, and 
                          reporting requirements.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Step 6: Training */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 to-[#F4C2C2]/20 flex items-center justify-center border border-[#D4AF37]/30">
                    <GraduationCap className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Training Modules</h2>
                    <p className="text-white/50 text-sm">Complete required training before starting</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {trainingModules.map(module => {
                    const isCompleted = formData.training_completed.includes(module.id);
                    return (
                      <div 
                        key={module.id}
                        className={`p-4 rounded-xl border transition-all ${
                          isCompleted 
                            ? 'border-green-500/30 bg-green-500/10' 
                            : 'border-white/10 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              isCompleted ? 'bg-green-500/20' : 'bg-white/5'
                            }`}>
                              {isCompleted ? (
                                <CheckCircle className="w-5 h-5 text-green-400" />
                              ) : (
                                <BookOpen className="w-5 h-5 text-white/50" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium flex items-center gap-2">
                                {module.title}
                                {module.required && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">REQUIRED</span>
                                )}
                              </p>
                              <p className="text-xs text-white/50 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {module.duration}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => completeTraining(module.id)}
                            disabled={isCompleted}
                            className={isCompleted ? 'bg-green-500/20 text-green-400' : 'bg-white/10 hover:bg-white/20 text-white'}
                            data-testid={`training-${module.id}`}
                          >
                            {isCompleted ? 'Completed' : 'Start'}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="text-center pt-4">
                  <p className="text-white/50 text-sm">
                    {formData.training_completed.length} of {trainingModules.filter(m => m.required).length} required modules completed
                  </p>
                </div>
              </div>
            )}

            {/* Step 7: Review */}
            {currentStep === 7 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 to-[#F4C2C2]/20 flex items-center justify-center border border-[#D4AF37]/30">
                    <Eye className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Review Your Application</h2>
                    <p className="text-white/50 text-sm">Please verify all information is correct</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Account Info */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <h4 className="text-sm font-medium text-[#D4AF37] mb-3">Account Information</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-white/50">Name</p>
                        <p className="text-white">{formData.full_name}</p>
                      </div>
                      <div>
                        <p className="text-white/50">Email</p>
                        <p className="text-white">{formData.email}</p>
                      </div>
                      <div>
                        <p className="text-white/50">Phone</p>
                        <p className="text-white">{formData.phone}</p>
                      </div>
                      <div>
                        <p className="text-white/50">Location</p>
                        <p className="text-white">{formData.city}, {formData.state}</p>
                      </div>
                    </div>
                  </div>

                  {/* Services */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <h4 className="text-sm font-medium text-[#D4AF37] mb-3">Selected Services</h4>
                    <div className="flex flex-wrap gap-2">
                      {formData.service_types.map(serviceId => {
                        const service = SERVICE_OPTIONS.find(s => s.id === serviceId);
                        return service ? (
                          <span 
                            key={serviceId}
                            className="px-3 py-1 rounded-full text-sm"
                            style={{ background: `${service.color}20`, color: service.color }}
                          >
                            {service.label}
                          </span>
                        ) : null;
                      })}
                    </div>
                    <p className="text-white/50 text-sm mt-2">{formData.experience_years} years experience</p>
                  </div>

                  {/* Documents Status */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <h4 className="text-sm font-medium text-[#D4AF37] mb-3">Documents & Verification</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${formData.id_document ? 'text-green-400' : 'text-white/30'}`} />
                        <span className="text-white/80 text-sm">Government ID</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${formData.background_consent ? 'text-green-400' : 'text-white/30'}`} />
                        <span className="text-white/80 text-sm">Background Check Consent</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${formData.agreed_to_policies ? 'text-green-400' : 'text-white/30'}`} />
                        <span className="text-white/80 text-sm">Policies Accepted</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${formData.training_completed.length >= trainingModules.filter(m => m.required).length ? 'text-green-400' : 'text-white/30'}`} />
                        <span className="text-white/80 text-sm">Training Completed</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                  <p className="text-amber-400 text-sm">
                    <strong>Note:</strong> By submitting, your application will be reviewed by our team. 
                    This typically takes 1-3 business days. You'll receive an email when approved.
                  </p>
                </div>
              </div>
            )}

            {/* Step 8: Complete */}
            {currentStep === 8 && (
              <div className="text-center py-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-24 h-24 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] flex items-center justify-center mx-auto mb-6"
                >
                  <Award className="w-12 h-12 text-black" />
                </motion.div>
                
                <h2 className="text-3xl font-bold text-white font-['Cormorant_Garamond'] mb-2">
                  Application Submitted!
                </h2>
                <p className="text-white/60 mb-8 max-w-md mx-auto">
                  Thank you for applying to join NC Help Hub. Our team will review your application 
                  and get back to you within 1-3 business days.
                </p>

                <div className="p-6 rounded-2xl bg-white/5 border border-white/10 max-w-md mx-auto mb-8">
                  <h3 className="text-white font-medium mb-4">What happens next?</h3>
                  <div className="space-y-3 text-left">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-[#D4AF37] text-xs font-bold">1</span>
                      </div>
                      <p className="text-white/70 text-sm">We'll verify your documents and run a background check</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-[#D4AF37] text-xs font-bold">2</span>
                      </div>
                      <p className="text-white/70 text-sm">You'll receive an email with your application status</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-[#D4AF37] text-xs font-bold">3</span>
                      </div>
                      <p className="text-white/70 text-sm">Once approved, log in to start accepting jobs!</p>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={() => navigate('/')} 
                  className="bg-gradient-to-r from-[#D4AF37] to-[#F4C2C2] text-black font-bold rounded-xl px-8"
                  data-testid="back-to-home-btn"
                >
                  Back to Home
                </Button>
              </div>
            )}

            {/* Navigation Buttons */}
            {currentStep < 8 && (
              <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
                {currentStep > 1 ? (
                  <Button
                    variant="outline"
                    onClick={prevStep}
                    className="border-white/20 text-white hover:bg-white/10 rounded-xl px-6"
                    data-testid="prev-step-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                ) : (
                  <div />
                )}
                
                {currentStep < 7 ? (
                  <Button
                    onClick={nextStep}
                    className="bg-gradient-to-r from-[#D4AF37] to-[#F4C2C2] text-black font-bold rounded-xl px-6"
                    data-testid="next-step-btn"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={submitApplication}
                    disabled={loading}
                    className="bg-gradient-to-r from-[#D4AF37] to-[#F4C2C2] text-black font-bold rounded-xl px-8"
                    data-testid="submit-application-btn"
                  >
                    {loading ? 'Submitting...' : 'Submit Application'}
                    <Zap className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default WorkerOnboardingWizard;
